import React, { useState, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import type {
  BubbleDataPoint,
  Point,
  ChartEvent,
  ActiveElement,
} from 'chart.js';
import { Box, Grid2 } from '@mui/material';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import type { MetricCardData } from '../../API/services/metricsService';

ChartJS.register(ArcElement, Tooltip);

interface DoughnutChartProps {
  donutData: MetricCardData;
}

function getStatusColor(status: string) {
  switch (status) {
    case 'new':
      return '#8AA3CA';
    case 'in progress':
      return '#F2D398';
    case 'call started':
      return '#AFC9C1';
    case 'call completed':
      return '#BBB5CD';
    case 'completed':
      return '#CDD2DD';
    case 'canceled':
      return '#F2C3CC';
    case 'accepted':
      return '#21BB6E';
    case 'rejected':
      return '#E6ECF1';
    default:
      return '#E6ECF1'; // Default color if status is not found
  }
}

const DoughnutChart: React.FC<DoughnutChartProps> = ({ donutData }) => {
  const statusData = donutData.donutChart.statusData;
  const acceptData = donutData.donutChart.acceptData;

  const [hoveredStatus, setHoveredStatus] = useState<{
    label: string;
    value: string;
    textcolor: string;
    x: number;
    y: number;
  } | null>(null);

  const [hoveredAcceptStatus, setHoveredAcceptStatus] = useState<{
    label: string;
    value: string;
    textcolor: string;
    x: number;
    y: number;
  } | null>(null);

  const chartRef = useRef<ChartJS<
    'doughnut',
    (number | Point | [number, number] | BubbleDataPoint | null)[],
    unknown
  > | null>(null);
  const chartAcceptRef = useRef<ChartJS<
    'doughnut',
    (number | Point | [number, number] | BubbleDataPoint | null)[],
    unknown
  > | null>(null);

  const total = statusData.reduce((acc, item) => acc + item.value, 0);

  const totalAccept = donutData.totalRequest;

  const isStatusZero = statusData.every((val) => val.value === 0);
  const color = statusData.map((item) =>
    getStatusColor(item.label.toLowerCase())
  );

  const data = {
    datasets: [
      {
        data: isStatusZero ? [1] : statusData.map((item) => item.value),
        backgroundColor: isStatusZero ? ['#E6ECF1'] : color,

        hoverOffset: 4,
        borderWidth: 0,
      },
    ],
  };

  const isAcceptZero = acceptData.every((val) => val.value === 0);
  const colorAccept = acceptData.map((item) =>
    getStatusColor(item.label.toLowerCase())
  );
  const dataAccept = {
    datasets: [
      {
        data: isAcceptZero ? [1] : acceptData.map((item) => item.value),
        backgroundColor: isAcceptZero ? ['#E6ECF1'] : colorAccept,
        hoverOffset: 4,
        borderWidth: 0,
      },
    ],
  };

  const handleHover = (
    _event: ChartEvent,
    elements: ActiveElement[],
    type: 'status' | 'accept'
  ) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const chartInstance =
        type === 'status' ? chartRef.current : chartAcceptRef.current;

      if (!chartInstance) return;

      const chartCanvas = chartInstance.canvas.getBoundingClientRect();
      const element = chartInstance.getDatasetMeta(0).data[index];

      if (!element) return;

      const tooltipPos = element?.tooltipPosition(true);

      if (!tooltipPos || tooltipPos.x === null || tooltipPos.y === null) return;
      const x = chartCanvas.left + tooltipPos.x + window.scrollX;
      const y = chartCanvas.top + tooltipPos.y + window.scrollY;

      if (type === 'status') {
        setHoveredStatus({
          label: statusData[index].label,
          value: `${statusData[index].value}`,
          textcolor: getStatusColor(statusData[index].label.toLowerCase()),
          x,
          y,
        });
      } else {
        setHoveredAcceptStatus({
          label: acceptData[index].label,
          value: `${acceptData[index].value}`,
          textcolor: getStatusColor(acceptData[index].label.toLowerCase()),
          x,
          y,
        });
      }
    } else {
      type === 'status' ? setHoveredStatus(null) : setHoveredAcceptStatus(null);
    }
  };

  const options = {
    plugins: {
      tooltip: { enabled: false },
    },
    cutout: '70%',
    onHover: (event: ChartEvent, elements: ActiveElement[]) =>
      handleHover(event, elements, 'status'),
  };

  const optionsAccept = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      tooltip: { enabled: false },
    },
    cutout: '70%',
    onHover: (event: ChartEvent, elements: ActiveElement[]) =>
      handleHover(event, elements, 'accept'),
  };

  function getTransform(x: number, y: number): string {
    const chartCenterX =
      chartRef.current!.canvas.width / 2 +
      chartRef.current!.canvas.getBoundingClientRect().left;
    const chartCenterY =
      chartRef.current!.canvas.height / 2 +
      chartRef.current!.canvas.getBoundingClientRect().top;

    if (x < chartCenterX && y < chartCenterY) {
      // Top-left quadrant
      return 'translate(-100%, -100%)';
    } else if (x >= chartCenterX && y < chartCenterY) {
      // Top-right quadrant
      return 'translate(0%, -100%)';
    } else if (x < chartCenterX && y >= chartCenterY) {
      // Bottom-left quadrant
      return 'translate(-100%, 0%)';
    } else {
      // Bottom-right quadrant
      return 'translate(0%, 0%)';
    }
  }

  return (
    <Box className="flex items-center justify-center pie-container cursor-pointer">
      <Grid2 container spacing={7}>
        <Grid2 size={{ xs: 6, md: 6, lg: 6, xl: 6 }}>
          <Box className="pie-single">
            <Box className="pie-chat-responsive">
              <Doughnut
                ref={chartAcceptRef}
                data={dataAccept}
                options={optionsAccept}
              />
            </Box>
            <Box className="pie-text">
              <CustomTypography id="patient-name" variant="h4">
                {hoveredAcceptStatus ? (
                  <Box>
                    <span style={{ color: hoveredAcceptStatus?.textcolor }}>
                      {hoveredAcceptStatus.value}
                    </span>
                    <span>{`/${totalAccept}`}</span>
                  </Box>
                ) : (
                  <span>{`-/${totalAccept}`}</span>
                )}
              </CustomTypography>
            </Box>
            <CustomTypography
              id="patient-name"
              variant="body2"
              color="textPrimary"
              className="pt-12"
            >
              Accepted
            </CustomTypography>
          </Box>
          {hoveredAcceptStatus &&
            hoveredAcceptStatus.label !== '' &&
            totalAccept > 0 && (
              <Box
                className="doughnut-hover"
                style={{
                  top: hoveredAcceptStatus.y,
                  left: hoveredAcceptStatus.x,
                  transform: getTransform(
                    hoveredAcceptStatus.x,
                    hoveredAcceptStatus.y
                  ),
                }}
              >
                {hoveredAcceptStatus.label}
              </Box>
            )}
        </Grid2>

        <Grid2 size={{ xs: 6, md: 6, lg: 6, xl: 6 }}>
          <Box className="pie-single">
            <Box className="pie-chat-responsive">
              <Doughnut ref={chartRef} data={data} options={options} />
            </Box>
            <Box className="pie-text">
              <CustomTypography id="patient-name" variant="h4">
                {hoveredStatus ? (
                  <Box>
                    <span style={{ color: hoveredStatus?.textcolor }}>
                      {hoveredStatus.value}
                    </span>
                    <span>{`/${total}`}</span>
                  </Box>
                ) : (
                  <span>{`-/${total}`}</span>
                )}
              </CustomTypography>
            </Box>
            <CustomTypography
              id="patient-name"
              variant="body2"
              color="textPrimary"
              className="pt-12"
            >
              Status
            </CustomTypography>
          </Box>
          {hoveredStatus && (
            <Box
              className="doughnut-hover"
              style={{
                top: hoveredStatus.y,
                left: hoveredStatus.x,
                transform: getTransform(hoveredStatus.x, hoveredStatus.y),
              }}
            >
              {hoveredStatus.label}
            </Box>
          )}
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default DoughnutChart;
